import { useMemo } from 'react';

import {
  PlatformTableColumnListItem,
  usePlatformListAllCustomColumns,
  usePlatformTableSchemaTeam,
} from '@cloudquery/api-platform';
import { InfoCircleIcon } from '@cloudquery/base-icons';
import { FeatureFlag, useFeatureFlags, useBaseAuthenticated } from '@cloudquery/base-users';
import EditIcon from '@mui/icons-material/Edit';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Tooltip from '@mui/material/Tooltip';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { CLOUD_ASSET_TABLE_NAME, dayjs } from 'shared/base';

import {
  CloudAssetColumns,
  CloudAssetSharedColumns,
} from '../components/table/columns/cloudAssetColumns';
import { AssetInventorySidebarFilterFieldEnum } from '../constants/sidebar';
import { AssetInventoryGridColDef, DataFilterString, FilterFieldProps } from '../types';
import {
  getDefaultColumns,
  getDefaultColumnMinWidth,
  getHeaderName,
  getDefaultColumnMaxWidth,
  orderColumns,
} from '../utils/table';
import { getTableNameFromFilter } from '../utils/tableState';

export const useAssetInventorySchema = (
  filter: DataFilterString,
): {
  columns: AssetInventoryGridColDef[];
  defaultDisplayColumns: string[];
  filterFields: FilterFieldProps[];
  hasEditableCustomColumns: boolean;
  isReady: boolean;
  tableName: string;
} => {
  const { currentMembership } = useBaseAuthenticated();

  const { featureFlags } = useFeatureFlags();
  const {
    editing: allowEditingCustomColumns,
    enabled: displayCustomColumns,
    externals: displayExternalCustomColumns,
  } = featureFlags[FeatureFlag.ALLOWED_CUSTOM_COLUMNS];

  const tableName = getTableNameFromFilter(filter);

  const { data: schemaResponse, isFetched } = usePlatformTableSchemaTeam(
    currentMembership.team.name,
    tableName,
  );

  const { data: customColumnData } = usePlatformListAllCustomColumns(currentMembership.team.name, {
    sort_by: ['label'],
    sort_dir: ['asc'],
  });

  return useMemo(() => {
    const filterFields = [];
    const columns = [];
    const cloudAssetSharedColumnFields = new Set(CloudAssetSharedColumns.map(({ field }) => field));
    const hasEditableCustomColumns =
      customColumnData?.data?.items?.some((customColumn) => {
        return customColumn.value_type === 'external' && allowEditingCustomColumns;
      }) ?? false;

    const filteredSchema =
      schemaResponse?.data?.schema
        .map((field) => {
          if (tableName === CLOUD_ASSET_TABLE_NAME) {
            return {
              ...field,
              name:
                AssetInventorySidebarFilterFieldEnum[
                  field.name as keyof typeof AssetInventorySidebarFilterFieldEnum
                ] ?? field.name,
            };
          }

          return field;
        })
        .filter(
          ({ name }: PlatformTableColumnListItem) =>
            name.slice(0, 1) !== '_' || cloudAssetSharedColumnFields.has(name.replace('_cq_', '')),
        ) ?? [];

    for (const field of filteredSchema) {
      filterFields.push({
        field: field.name,
        label: field.name,
        nullable: field.nullable,
        type: field.kind,
      } as FilterFieldProps);
      columns.push({
        field: field.name,
        headerName: getHeaderName(field.name),
        maxWidth: getDefaultColumnMaxWidth(),
        minWidth: getDefaultColumnMinWidth(field.name),
      } as AssetInventoryGridColDef);
    }

    // only push into array if the feature flag is enabled
    const customColumns = [];
    if (displayCustomColumns) {
      for (const customColumn of customColumnData?.data?.items ?? []) {
        const isExternal = customColumn.value_type === 'external';
        const isEditable = isExternal && allowEditingCustomColumns;

        if (!displayExternalCustomColumns && isExternal) {
          continue;
        }

        customColumns.push({
          columnType: customColumn.column_type,
          description: customColumn.description,
          editable: isEditable,
          field: customColumn.column_name,
          headerName: getHeaderName(customColumn.label),
          id: customColumn.id,
          isCustomColumn: true,
          maxWidth: getDefaultColumnMaxWidth(),
          minWidth: getDefaultColumnMinWidth(`${customColumn.label}  (i)`),
          renderCell: (params) => {
            let displayValue = params.row[params.field];
            if (customColumn.column_type === 'DateTime') {
              displayValue = displayValue ? dayjs(displayValue).format('YYYY-MM-DD') : '';
            }

            return (
              <Stack
                alignItems="center"
                direction="row"
                spacing={1}
                sx={{
                  ...(isEditable && {
                    '& .MuiSvgIcon-root': {
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    '&:hover .MuiSvgIcon-root': {
                      opacity: 1,
                    },
                  }),
                  height: '100%',
                }}
              >
                <span>{displayValue}</span>
                {isEditable && <EditIcon fontSize="small" />}
              </Stack>
            );
          },
          renderEditCell: (params) => {
            switch (customColumn.column_type) {
              case 'DateTime': {
                return (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    dateFormats={{ keyboardDate: 'YYYY-MM-DD' }}
                  >
                    <DatePicker
                      closeOnSelect={true}
                      onChange={(newValue) => {
                        params.api.setEditCellValue({
                          field: params.field,
                          id: params.id,
                          value: newValue,
                        });
                      }}
                      onClose={() => {
                        // save value when selecting a date and closing the calendar
                        params.api.stopCellEditMode({ field: params.field, id: params.id });
                      }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            disableUnderline: true,
                            sx: {
                              padding: 1,
                              typography: 'body2',
                            },
                          },
                          onKeyDown: (e) => {
                            // force enter key to close date picker and save value
                            if (e.key === 'Enter') {
                              params.api.stopCellEditMode({ field: params.field, id: params.id });
                            }
                          },
                          variant: 'standard',
                        },
                      }}
                      value={params.value || null}
                    />
                  </LocalizationProvider>
                );
              }
              default: {
                return (
                  <TextField
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        input: {
                          padding: 1,
                        },
                      },
                    }}
                    onChange={(event) => {
                      const newValue = event.target.value;
                      params.api.setEditCellValue({
                        field: params.field,
                        id: params.id,
                        value: newValue,
                      });
                    }}
                    value={params.value || ''}
                    variant="standard"
                  />
                );
              }
            }
          },
          renderHeader: (item) => (
            <Stack alignItems="center" direction="row" spacing={1}>
              <span>{item.colDef.headerName}</span>
              {item.colDef.description && (
                <Tooltip placement="bottom" title={item.colDef.description}>
                  <span>
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              )}
            </Stack>
          ),
        } as AssetInventoryGridColDef);
      }
    }

    if (tableName === CLOUD_ASSET_TABLE_NAME) {
      return {
        columns: [...CloudAssetColumns, ...customColumns],
        defaultDisplayColumns: [
          ...CloudAssetColumns.map(({ field }) => field),
          ...customColumns.map(({ field }) => field),
        ],
        filterFields,
        hasEditableCustomColumns,
        isReady: isFetched,
        tableName,
      };
    }

    return {
      columns: orderColumns(
        columns,
        CloudAssetSharedColumns,
        schemaResponse?.data?.default_columns,
      ),
      defaultDisplayColumns: getDefaultColumns(
        columns,
        CloudAssetSharedColumns,
        schemaResponse?.data?.default_columns,
      ),
      filterFields,
      hasEditableCustomColumns,
      isReady: isFetched,
      tableName,
    };
  }, [
    schemaResponse,
    tableName,
    isFetched,
    customColumnData,
    displayCustomColumns,
    allowEditingCustomColumns,
    displayExternalCustomColumns,
  ]);
};
